import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiClock, FiStar, FiMapPin } from 'react-icons/fi';

const FeaturesSection = styled.section`
  background: #f8fafb;
  width: 100%;
  padding: 100px 20px;
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #DA015C;
  margin-bottom: 16px;
  text-align: center;
`;

const SectionHeading = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  color: #0a174e;
  text-align: center;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 72px;
  span {
    color: #DA015C;
  }
`;

const FeatureBlock = styled.div<{ reverse?: boolean }>`
  display: flex;
  flex-direction: ${p => p.reverse ? 'row-reverse' : 'row'};
  align-items: center;
  gap: 80px;
  margin-bottom: 100px;
  &:last-child {
    margin-bottom: 0;
  }
  @media (max-width: 860px) {
    flex-direction: column;
    gap: 40px;
    margin-bottom: 64px;
    text-align: center;
  }
`;

const TextBlock = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media (max-width: 860px) {
    align-items: center;
  }
`;

const TagBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(10,23,78,0.06);
  border-radius: 100px;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #0a174e;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 900;
  color: #0a174e;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin-bottom: 16px;
  span {
    color: #DA015C;
  }
`;

const FeatureDesc = styled.p`
  font-size: 1.05rem;
  color: #5a6a7c;
  line-height: 1.8;
  max-width: 460px;
  margin-bottom: 28px;
  @media (max-width: 860px) {
    max-width: 100%;
  }
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0a174e;
  @media (max-width: 860px) {
    justify-content: center;
  }
`;

const BulletDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #DA015C;
  flex-shrink: 0;
`;

const ImageBlock = styled(motion.div)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const FeatureImg = styled.img`
  width: 100%;
  max-width: 480px;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(10,23,78,0.12);
  object-fit: cover;
  display: block;
`;

const ImgAccent = styled.div<{ top?: string; bottom?: string; left?: string; right?: string }>`
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #DA015C, #0a174e);
  opacity: 0.12;
  top: ${p => p.top};
  bottom: ${p => p.bottom};
  left: ${p => p.left};
  right: ${p => p.right};
  z-index: 0;
`;

const features = [
  {
    tag: '⚡ Always Ready',
    title: <>Available <span>24 hours</span>, 7 days a week</>,
    desc: "Whether it's an early morning commute or a late-night trip home, Wassel is always there when you need a ride — no waiting, no excuses.",
    bullets: ['Instant driver matching', 'No peak-hour cutoffs', 'Real-time availability'],
    image: '/images/app-booking.png',
    icon: <FiClock />,
    reverse: false,
  },
  {
    tag: '🏅 Top-Rated Drivers',
    title: <>Professional, <span>verified drivers</span></>,
    desc: 'Every Wassel driver goes through a thorough background check and professional onboarding. Expect clean vehicles, courteous service, and smooth rides.',
    bullets: ['Background screened', 'Rated after every ride', 'Trained & professional'],
    image: '/images/wassel-driver.jpg',
    icon: <FiStar />,
    reverse: true,
  },
  {
    tag: '📍 Always Informed',
    title: <>Live <span>GPS tracking</span> on every ride</>,
    desc: 'Follow your driver in real-time on the map. Share your trip with family and friends, and always know exactly when you will arrive.',
    bullets: ['Real-time map tracking', 'Trip sharing with contacts', 'Accurate ETA updates'],
    image: '/images/Real-time-Tracking.png',
    icon: <FiMapPin />,
    reverse: false,
  },
];

const FeaturesEn: React.FC = () => (
  <FeaturesSection>
    <Inner>
      <Eyebrow>How It Works</Eyebrow>
      <SectionHeading>Everything you need, <span>nothing you don't</span></SectionHeading>

      {features.map((f, i) => (
        <FeatureBlock key={i} reverse={f.reverse}>
          <TextBlock
            initial={{ opacity: 0, x: f.reverse ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <TagBadge>{f.tag}</TagBadge>
            <FeatureTitle>{f.title}</FeatureTitle>
            <FeatureDesc>{f.desc}</FeatureDesc>
            <BulletList>
              {f.bullets.map((b, j) => (
                <BulletItem key={j}><BulletDot />{b}</BulletItem>
              ))}
            </BulletList>
          </TextBlock>

          <ImageBlock
            initial={{ opacity: 0, x: f.reverse ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
          >
            <ImgAccent top="-20px" left="-20px" />
            <ImgAccent bottom="-20px" right="-20px" />
            <FeatureImg
              src={f.image}
              alt={typeof f.title === 'string' ? f.title : ''}
              loading="lazy"
            />
          </ImageBlock>
        </FeatureBlock>
      ))}
    </Inner>
  </FeaturesSection>
);

export default FeaturesEn;
